/**
 * Decimal-string money.
 *
 * Money is `NUMERIC(18,4)` in Postgres and a decimal string over the wire
 * (`"749.5000"`). It must never become a JS `number` — not for arithmetic and
 * not even for display. `Number("0.1") + Number("0.2")` is `0.30000000000000004`,
 * and a panel that shows a player a balance one paisa off the ledger is a panel
 * an operator stops trusting.
 *
 * So every operation here is exact integer arithmetic on `bigint`, scaled by
 * `SCALE` decimal places. Nothing in this file calls `parseFloat`, `Number()`
 * or `toFixed()` on an amount, and nothing outside it should either.
 *
 * Ratios (realised hold, share of turnover) are the one place a division is
 * needed. That is done in `bigint` too, at a wider scale, and returned as a
 * decimal string — never as a float percentage.
 */

/** Storage scale. Matches NUMERIC(18,4) on the server. */
export const SCALE = 4;

/** Scale used for ratios before they are rounded for display. */
const RATIO_SCALE = 8;

const TEN = 10n;

function pow10(n: number): bigint {
  let r = 1n;
  for (let i = 0; i < n; i += 1) r *= TEN;
  return r;
}

const UNIT = pow10(SCALE);

export class MoneyError extends Error {}

/**
 * Parse a decimal string into a scaled bigint. Accepts an optional sign, any
 * number of integer digits, and up to `scale` fractional digits; extra
 * fractional digits are an error rather than a silent truncation, because a
 * silently truncated amount is a money bug that looks like a rounding choice.
 */
export function toUnits(value: string, scale: number = SCALE): bigint {
  const raw = value.trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) {
    throw new MoneyError(`Not a decimal string: ${JSON.stringify(value)}`);
  }
  const sign = m[1] === '-' ? -1n : 1n;
  const whole = m[2] === '' ? '0' : (m[2] as string);
  const frac = m[3] ?? '';
  if (frac.length > scale) {
    throw new MoneyError(`More than ${scale} decimal places: ${JSON.stringify(value)}`);
  }
  const padded = frac.padEnd(scale, '0');
  return sign * (BigInt(whole) * pow10(scale) + BigInt(padded === '' ? '0' : padded));
}

/** Render a scaled bigint back to a plain decimal string. */
export function fromUnits(units: bigint, scale: number = SCALE): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const divisor = pow10(scale);
  const whole = abs / divisor;
  const frac = abs % divisor;
  const fracText = scale === 0 ? '' : `.${frac.toString().padStart(scale, '0')}`;
  return `${negative ? '-' : ''}${whole}${fracText}`;
}

/** True when the string is a well-formed amount. */
export function isAmount(value: string, scale: number = SCALE): boolean {
  try {
    toUnits(value, scale);
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------ arithmetic

export function add(a: string, b: string): string {
  return fromUnits(toUnits(a) + toUnits(b));
}

export function sub(a: string, b: string): string {
  return fromUnits(toUnits(a) - toUnits(b));
}

export function neg(a: string): string {
  return fromUnits(-toUnits(a));
}

export function abs(a: string): string {
  const u = toUnits(a);
  return fromUnits(u < 0n ? -u : u);
}

/** -1, 0 or 1. Comparison without ever leaving exact arithmetic. */
export function cmp(a: string, b: string): -1 | 0 | 1 {
  const x = toUnits(a);
  const y = toUnits(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function isNegative(a: string): boolean {
  return toUnits(a) < 0n;
}

export function isZero(a: string): boolean {
  return toUnits(a) === 0n;
}

/** Sum a column. Empty sums to zero, so a table with no rows still renders. */
export function sum(values: readonly string[]): string {
  let total = 0n;
  for (const v of values) total += toUnits(v);
  return fromUnits(total);
}

// ---------------------------------------------------------------------- ratios

/**
 * `numerator / denominator` as a decimal string with `places` decimals,
 * half-up. Returns null when the denominator is zero — a hold percentage on
 * zero turnover is undefined, and rendering it as "0.00%" would be a lie an
 * operator might act on.
 */
export function ratio(
  numerator: string,
  denominator: string,
  places = RATIO_SCALE,
): string | null {
  const d = toUnits(denominator);
  if (d === 0n) return null;
  const n = toUnits(numerator) * pow10(places);
  return fromUnits(roundedDiv(n, d), places);
}

/** Same as `ratio`, expressed in percent. */
export function percent(numerator: string, denominator: string, places = 2): string | null {
  const r = ratio(numerator, denominator, places + 2);
  if (r === null) return null;
  return fromUnits(roundHalfUp(toUnits(r, places + 2) * 100n, 2), places);
}

/** Half-up division on bigints, sign-aware. */
function roundedDiv(n: bigint, d: bigint): bigint {
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = an / ad;
  const r = an % ad;
  const rounded = r * 2n >= ad ? q + 1n : q;
  return negative ? -rounded : rounded;
}

/** Drop `drop` decimal places from a scaled bigint, half-up. */
function roundHalfUp(units: bigint, drop: number): bigint {
  if (drop <= 0) return units;
  return roundedDiv(units, pow10(drop));
}

/**
 * A fraction from the database (`games.target_edge`, e.g. `"0.0200"`) as a
 * percentage string (`"2.00"`). Still exact: a shift of the decimal point.
 */
export function fractionToPercent(fraction: string, places = 2): string {
  const units = toUnits(fraction, 6) * 100n;
  return fromUnits(roundHalfUp(units, 6 - places), places);
}

// -------------------------------------------------------------------- display

const GROUPING = /\B(?=(\d{3})+(?!\d))/g;

export interface FormatOptions {
  /** Decimal places to show. Storage keeps 4; a screen usually wants 2. */
  places?: number;
  /** Thousands separators. On by default — long figures are unreadable without. */
  group?: boolean;
  /** Always show a sign, so a ledger column reads at a glance. */
  signed?: boolean;
}

/**
 * Format an amount for display. String in, string out — the value is never
 * converted to a number on the way through, so what is rendered is exactly
 * what the ledger holds, rounded half-up only where places are dropped.
 */
export function format(value: string, options: FormatOptions = {}): string {
  const places = options.places ?? 2;
  const group = options.group ?? true;
  const rounded = fromUnits(roundHalfUp(toUnits(value), SCALE - places), places);

  const negative = rounded.startsWith('-');
  const bare = negative ? rounded.slice(1) : rounded;
  const dot = bare.indexOf('.');
  const whole = dot === -1 ? bare : bare.slice(0, dot);
  const frac = dot === -1 ? '' : bare.slice(dot);
  const grouped = group ? whole.replace(GROUPING, ',') : whole;

  const sign = negative ? '-' : options.signed ? '+' : '';
  return `${sign}${grouped}${frac}`;
}

/**
 * Formatted with the currency mark. The sign stays outside the mark — "-₹1,000"
 * rather than "₹-1,000" — because a column of amounts is scanned down its left
 * edge and the sign is the first thing that must register.
 */
export function formatCurrency(value: string, options: FormatOptions = {}): string {
  const text = format(value, options);
  if (text.startsWith('-')) return `-₹${text.slice(1)}`;
  if (text.startsWith('+')) return `+₹${text.slice(1)}`;
  return `₹${text}`;
}

/** A percentage string (`"2.35"`) with its sign and mark (`"+2.35%"`). */
export function formatPercent(
  value: string | null,
  options: { places?: number; signed?: boolean } = {},
): string {
  if (value === null) return '—';
  const places = options.places ?? 2;
  const units = toUnits(value, SCALE);
  const text = fromUnits(roundHalfUp(units, SCALE - places), places);
  const sign = options.signed && !text.startsWith('-') && toUnits(text, places) !== 0n ? '+' : '';
  return `${sign}${text}%`;
}

/** Compact form for a dashboard tile: 1.24 Cr / 12.4 L / 45.2 K. Exact input,
 * rounded output — used only where the precise figure is one hover away. */
export function formatCompact(value: string): string {
  const units = toUnits(value);
  const negative = units < 0n;
  const a = negative ? -units : units;
  const sign = negative ? '-' : '';
  const crore = 10000000n * UNIT;
  const lakh = 100000n * UNIT;
  const thousand = 1000n * UNIT;

  if (a >= crore) return `${sign}${scaleDown(a, crore)} Cr`;
  if (a >= lakh) return `${sign}${scaleDown(a, lakh)} L`;
  if (a >= thousand) return `${sign}${scaleDown(a, thousand)} K`;
  return format(fromUnits(units), { places: 2 });
}

function scaleDown(units: bigint, divisor: bigint): string {
  return fromUnits(roundedDiv(units * 100n, divisor), 2);
}
